import "./BartrackOverview.scss";
import OnTapIcon from "../../../../images/onTap.png";
import InventoryIcon from "../../../../images/inventory.png";
import CombinedValueIcon from "../../../../images/combinedValue.png";
import BartrackIcon from "../../../../images/cooler.png";
import { useEffect, useState } from "react";
import BeerIcon from "../../../../images/icons/beerColored.png";
import KegIcon from "../../../../images/icons/keg.png";
import { useNavigate } from "react-router-dom";
import Table from "../../../../components/table/Table";
import { useTranslation } from "react-i18next";
import ChartCard from "../../../../components/ChartCard/ChartCard";
import getBartrackPoursSummary from "../../../../utility/getBartrackPoursSummary";
import getBartrackQualitySummary from "../../../../utility/getBartrackQualitySummary";
import * as XLSX from "xlsx";
import { getOrgProducts } from "../../../../utility";
import getIcon from "../../../../utility/getIcon";
import { getCollection } from "../../../../utility/getCollection";
import getBartrackLines from "../../../../utility/getBartrackLines";
import LineDefault from "../../../../images/LineDefault.png";
import GradientButton from "../../../../components/buttons/gradientButton/GradientButton";
import getOrgOrders from "../../../../utility/getOrgOrders";
import DatePicker from "react-datepicker";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import Skeleton from "../../../../components/skeletonLoader/SkeletonLoader";
dayjs.extend(utc);

const DEFAULT_BINS = 168;
const DEFAULT_DAYS_PERIOD = 7;
const BEER_EQUIVALENT_DIVISOR = 330;
const DATE_FORMAT_ROUNDED = "YYYY-MM-DDTHH:00:00[Z]";

function BartrackOverview({ org }) {
  const [beverages, setBeverages] = useState([]);
  const [bartracks, setBartracks] = useState([]);
  const [qualitySummary, setQualitySummary] = useState([]);
  const [currentBartrack, setCurrentBartrack] = useState({});
  const [isQualityManagement, setIsQualityManagement] = useState(true);
  const [lines, setLines] = useState({ data: [], pourData: [] });
  const [hasError, setHasError] = useState(false);
  const [chartToShow, setChartToShow] = useState("humidity");
  const [ordersInformations, setOrdersInformations] = useState({
    totalEarn: 0,
    meanEarn: 0,
    biggestEarn: 0,
  });
  const [selectedPeriod, setSelectedPeriod] = useState(DEFAULT_DAYS_PERIOD);
  const [startDate, setStartDate] = useState(dayjs().subtract(DEFAULT_DAYS_PERIOD, "day").toDate());
  const [endDate, setEndDate] = useState(new Date());
  const [ordersVolumeByHour, setOrdersVolumeByHour] = useState({});
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isBartrackLoading, setIsBartrackDataLoading] = useState(true);
  const [isOrderingLoading, setIsOrderingLoading] = useState(true);
  const [isBartracksLoading, setIsBartracksLoading] = useState(true);
  const [isLinesLoading, setIsLinesLoading] = useState(true);
  const [isProductsLoading, setIsProductsLoading] = useState(true);

  const navigate = useNavigate();
  const { t } = useTranslation("bartrack");

  const periods = [
    { label: t("period_24h"), value: 1 },
    { label: t("period_7_days"), value: 7 },
    { label: t("period_14_days"), value: 14 },
    { label: t("period_30_days"), value: 30 },
  ];

  const setEmptyBartrackState = () => {
    setBeverages([]);
    setBartracks([]);
    setQualitySummary({});
    setCurrentBartrack({});
    setLines({ data: [], pourData: [] });
    setOrdersInformations({
      totalEarn: 0,
      meanEarn: 0,
      biggestEarn: 0,
    });
    setOrdersVolumeByHour({});
    setIsInitialLoading(false);
    setIsBartrackDataLoading(false);
    setIsOrderingLoading(false);
    setIsBartracksLoading(false);
    setIsLinesLoading(false);
    setIsProductsLoading(false);
  };

  useEffect(() => {
    if (!org?.id) return;

    if (org?.bartrackUUID) {
      fetchInformations();
    } else {
      setEmptyBartrackState();
    }
  }, [org]);

  async function fetchInformations() {
    try {
      const bartracks = await fetchBartrack();
      setBartracks(bartracks);
      setCurrentBartrack(bartracks[0]);
      setIsBartracksLoading(false);

      if (bartracks?.length > 0) {
        const products = await fetchOrgProducts();

        const lineIds = bartracks.flatMap((bartrack) => bartrack.lines?.map((line) => line.id));
        const matchedProducts = products?.filter((product) => {
          return product?.linesIds?.some((id) => lineIds.includes(id));
        });

        setBeverages(matchedProducts);
        setIsProductsLoading(false);

        const endDate = dayjs();
        const startDate = endDate.subtract(DEFAULT_DAYS_PERIOD, "days");

        const qualitySummary = await fetchQualitySummary(
          org?.bartrackUUID,
          bartracks[0]?.bartrackId,
          DEFAULT_BINS,
          startDate.toDate(),
          endDate.toDate(),
        );
        setQualitySummary(qualitySummary);
        setIsBartrackDataLoading(false);

        const linesWithProducts = bartracks.flatMap((bartrack) =>
          bartrack.lines.map((line) => {
            const lineProducts = matchedProducts.filter((product) =>
              product.linesIds?.includes(line.id),
            );
            return {
              ...line,
              bartrackUUID: bartrack.bartrackId,
              products: lineProducts,
            };
          }),
        );

        setLines({ data: linesWithProducts });
        setIsLinesLoading(false);

        const orders = await fetchOrders();
        const ordersInformation = {
          totalEarn: 0,
          biggestEarn: 0,
        };

        orders?.forEach((order) => {
          ordersInformation.totalEarn += order.totalAmount;

          if (ordersInformation.biggestEarn < order.totalAmount) {
            ordersInformation.biggestEarn = order.totalAmount;
          }
        });

        ordersInformation.meanEarn =
          orders?.length > 0 ? (ordersInformation.totalEarn / orders.length).toFixed(2) : 0;

        setOrdersInformations(ordersInformation);
        setIsOrderingLoading(false);
      } else {
        setBartracks(bartracks);
        setEmptyBartrackState();
      }

      setIsInitialLoading(false);
    } catch (error) {
      console.error(error);
    }
  }

  async function fetchOrders(startDate, endDate) {
    try {
      return getOrgOrders(org?.id, startDate, endDate);
    } catch (error) {
      console.error(error);
    }
  }

  async function fetchBartrack() {
    try {
      const bartrackInformations = await getCollection({
        collectionName: "Bartracks",
        orgId: org?.id,
      });

      const bartracks = bartrackInformations?.data || [];

      for (let index = 0; index < bartracks.length; index++) {
        const lines = await getBartrackLines({ bartrackId: bartracks[index]?.id });
        bartracks[index].lines = lines;
      }

      return bartracks;
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  async function fetchPoursSummary(uuid, startDate, endDate) {
    try {
      const newPoursSummary = await getBartrackPoursSummary(uuid, startDate, endDate);

      const updatedLines = lines?.data?.map((newLine) => {
        const matchLine = newPoursSummary.pumpsData?.find(
          (line) => line.line_id === newLine.bartrackId,
        );

        const products = beverages.filter((product) => product?.linesIds?.includes(newLine?.id));

        return {
          ...newLine,
          ...(matchLine || {}),
          products,
        };
      });

      setLines({ pourData: newPoursSummary.pourData, data: updatedLines });
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  async function fetchQualitySummary(uuid, bartrackUuid, bins, startDate, endDate) {
    try {
      return getBartrackQualitySummary(uuid, bartrackUuid, startDate, endDate, bins);
    } catch (error) {
      console.error(error);
    }
  }

  async function fetchOrgProducts() {
    try {
      const productsData = await getOrgProducts(org?.id);
      const productsArray = Array.isArray(productsData.products) ? productsData.products : [];

      return productsArray;
    } catch (error) {
      console.error(error);
    }
  }

  function exportLinesToExcel() {
    if (!lines || lines.data?.length === 0) {
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(lines.data);

    const colCount = Object.keys(lines.data[0]).length;
    worksheet["!cols"] = Array(colCount).fill({ wch: 30 });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Beverage Lines");
    XLSX.writeFile(workbook, `lines_${dayjs().format("YYYY-MM-DD")}.xlsx`);
  }

  function linesPourDataToChartValues() {
    const data =
      lines.pourData
        ?.filter((pour) => typeof pour.dateHour === "string")
        .map((pour) => ({
          date: pour.dateHour,
          value1: pour.totalVolumeMl || 0,
        })) || [];

    Object.keys(ordersVolumeByHour).forEach((date) => {
      const index = data.findIndex((currentData) => currentData.date === date);

      if (index !== -1) {
        data[index].value2 = ordersVolumeByHour[date];
      } else {
        data.push({ date, value2: ordersVolumeByHour[date] });
      }
    });

    return data;
  }

  function qualitySummaryDataToChartValues(data, colName) {
    if (!Array.isArray(data) || data.length === 0) return [];

    const newData = data
      .filter((pour) => typeof pour.date === "string")
      .map((pour) => ({
        date: pour.date,
        value1: pour[colName] || 0,
      }));

    return newData;
  }

  function Ordering({ value, name, icon, isLoading }) {
    if (isLoading) {
      return (
        <div className="ordering">
          <Skeleton className="ordering-skeleton-img" />
          <div className="ordering-skeleton-details">
            <Skeleton className="ordering-skeleton-details-value" />
            <Skeleton className="ordering-skeleton-details-name" />
          </div>
        </div>
      );
    }

    return (
      <div className="ordering">
        <img src={icon} className="ordering-img" alt="icon" />
        <div className="ordering-details">
          <p className="ordering-details-value">{value}</p>
          <p className="ordering-details-name">{name}</p>
        </div>
      </div>
    );
  }

  function StatsCardValues({ label, value, unit, isLoading }) {
    if (isLoading) {
      return (
        <div className="stats-card-skeleton-values-item">
          <Skeleton className="stats-card-skeleton-values-item-label" />
          <Skeleton className="stats-card-skeleton-values-item-value" />
        </div>
      );
    }
    return (
      <div className="stats-card-values-item">
        <p>{label}</p>
        <p>{value ? value + unit : "N/A"}</p>
      </div>
    );
  }

  function StatsCard({ title, latest, average, max, min, unit, isLoading }) {
    return (
      <div className="stats-card">
        <p>{title}</p>

        <div className="stats-card-values">
          <StatsCardValues label={t("latest")} value={latest} unit={unit} isLoading={isLoading} />
          <StatsCardValues label={t("average")} value={average} unit={unit} isLoading={isLoading} />
          <StatsCardValues label={t("max")} value={max} unit={unit} isLoading={isLoading} />
          <StatsCardValues label={t("min")} value={min} unit={unit} isLoading={isLoading} />
        </div>
      </div>
    );
  }

  function BeverageCard({ icon, name, onClick, isLoading }) {
    if (isLoading) {
      return (
        <div className="beverage-card-skeleton">
          <Skeleton className="beverage-card-skeleton-image" />
          <Skeleton className="beverage-card-skeleton-text" />
        </div>
      );
    }

    return (
      <div className="beverage-card" onClick={onClick}>
        <div className="beverage-card-container">
          <img src={icon} className="beverage-card-container-icon" alt="icon" />
        </div>
        <p className="beverage-card-name">{name}</p>
      </div>
    );
  }

  const BartrackItem = ({ bartrack, index, isLoading }) => {
    if (isLoading) {
      return (
        <div className="bartrack-skeleton">
          <Skeleton className="bartrack-skeleton-image" />
          <Skeleton className="bartrack-skeleton-text" />
        </div>
      );
    }

    return (
      <div
        className={`bartrack ${currentBartrack.id === bartrack.id ? "active" : ""}`}
        onClick={() => changeBartrack(index)}
      >
        <img src={bartrack?.imageUrl ?? BartrackIcon} alt="bartrack" />
        <p>{bartrack.name}</p>
      </div>
    );
  };

  const refreshData = async (isQualityManagement, startDate, endDate) => {
    if (!org?.bartrackUUID || bartracks.length === 0) {
      setEmptyBartrackState();
      setStartDate(startDate);
      setEndDate(endDate);
      return;
    }

    setIsBartrackDataLoading(true);
    setStartDate(startDate);
    setEndDate(endDate);
    const bins = Math.ceil(dayjs(endDate).diff(dayjs(startDate), "hour"));

    if (isQualityManagement) {
      const newQualitySummary = await fetchQualitySummary(
        org?.bartrackUUID,
        bartracks[0]?.bartrackId,
        bins,
        startDate,
        endDate,
      );
      setQualitySummary(newQualitySummary);
    } else {
      await fetchPoursSummary(org?.bartrackUUID, startDate, endDate);
      const orders = await fetchOrders(startDate.getTime(), endDate.getTime());
      const volumesByDateHour = {};

      orders.forEach((order) => {
        const dateHour = dayjs.utc(parseFloat(order.createdAt)).format(DATE_FORMAT_ROUNDED);
        const totalForOrder = order.products.reduce(
          (sum, p) => sum + p.servingSize * p.quantity,
          0,
        );

        volumesByDateHour[dateHour] = (volumesByDateHour[dateHour] || 0) + totalForOrder;
      });

      setOrdersVolumeByHour(volumesByDateHour);
    }

    setIsBartrackDataLoading(false);
  };

  function DatePickerHeader() {
    const periodShortcut = async (periodValue, isQualityManagement) => {
      const end = dayjs().toDate();
      const start = dayjs().subtract(periodValue, "day").toDate();

      setSelectedPeriod(periodValue);
      await refreshData(isQualityManagement, start, end);
    };

    return (
      <div className="date-picker">
        <div className="date-picker-periods">
          {periods.map((period) => (
            <button
              key={period.value}
              className={selectedPeriod === period.value ? "active" : ""}
              onClick={() => periodShortcut(period.value, isQualityManagement)}
            >
              {period.label}
            </button>
          ))}
        </div>

        <div className="date-picker-selectors">
          <div className="date-picker-selectors-selector">
            <label>{t("start_date")}</label>
            <DatePicker
              selected={startDate}
              onChange={(date) => {
                setSelectedPeriod(null);
                refreshData(isQualityManagement, date, endDate);
              }}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="Pp"
              placeholderText={t("select_start_date")}
              className="form-control"
              popperClassName="datepicker-popper"
              calendarClassName="datepicker-calendar"
            />
          </div>
          <div className="date-picker-selectors-selector">
            <label>{t("end_date")}</label>
            <DatePicker
              selected={endDate}
              onChange={(date) => {
                setSelectedPeriod(null);
                refreshData(isQualityManagement, startDate, date);
              }}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="Pp"
              placeholderText={t("select_end_date")}
              className="form-control"
              popperClassName="datepicker-popper"
              calendarClassName="datepicker-calendar"
            />
          </div>
        </div>
      </div>
    );
  }

  async function changeBartrack(bartrackIndex) {
    setCurrentBartrack(bartracks[bartrackIndex]);
    setIsBartrackDataLoading(true);

    const qualitySummary = await fetchQualitySummary(
      org?.bartrackUUID,
      bartracks[bartrackIndex]?.bartrackId,
      DEFAULT_BINS,
    );

    let updatedBartrack = [...bartracks];
    updatedBartrack[bartrackIndex] = {
      ...bartracks[bartrackIndex],
      ...qualitySummary,
    };

    setIsBartrackDataLoading(false);
    setBartracks(updatedBartrack);
    setCurrentBartrack(updatedBartrack[bartrackIndex]);
  }

  function navigateToBarTrack() {
    navigate("/dashboard/bartrack-setup", { state: { orgId: org.id } });
  }

  function navigateToPOS() {
    navigate("/dashboard/pos");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function navigateToLine(line) {
    navigate("/dashboard/bartrack-line-details", { state: { line, orgUUID: org.bartrackUUID } });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function handleStatisticsChange({ isQualityManagement }) {
    setIsQualityManagement(isQualityManagement);
    await refreshData(isQualityManagement, startDate, endDate);
  }

  return (
    <>
      <div className="bartrack-overview">
          {hasError && (
            <div className="bartrack-overview-error-popup-overlay">
              <div className="bartrack-overview-error-popup">
                <h3>{t("error_occurred")}</h3>
                <p>{t("cannot_load_data")}</p>
                <div className="bartrack-overview-error-popup-buttons">
                  <button onClick={() => fetchInformations()}>{t("retry")}</button>
                  <button onClick={() => setHasError(false)}>{t("close")}</button>
                </div>
              </div>
            </div>
          )}

        <section className="bartrack-overview-header">
            <div className="bartrack-overview-header-title">
              <p>BarTrack</p>
              <span>{t("overview_subtitle")}</span>
            </div>
            <button onClick={navigateToBarTrack}>{t("edit_information")}</button>
          </section>

          <section className="bartrack-overview-section">
            <p className="bartrack-overview-section-title">
              {t("ordering")} ({t("last_30_days")})
            </p>

            <div className="bartrack-overview-section-ordering">
              <Ordering
                value={`${ordersInformations?.totalEarn} €`}
                name={t("on_tap")}
                icon={OnTapIcon}
                isLoading={isOrderingLoading}
              />
              <Ordering
                value={`${ordersInformations?.meanEarn} €`}
                name={t("mean")}
                icon={InventoryIcon}
                isLoading={isOrderingLoading}
              />
              <Ordering
                value={`${ordersInformations?.biggestEarn} €`}
                name={t("highest_order")}
                icon={CombinedValueIcon}
                isLoading={isOrderingLoading}
              />
            </div>
          </section>

          <section className="bartrack-overview-section">
            <p className="bartrack-overview-section-title">{t("statistics")}</p>

            <div className="bartrack-overview-section-buttons">
              <button
                disabled={isInitialLoading}
                className={`${isQualityManagement ? "active" : ""}`}
                onClick={() => handleStatisticsChange({ isQualityManagement: true })}
              >
                {t("quality_management")}
              </button>
              <button
                disabled={isInitialLoading}
                className={`${isQualityManagement ? "" : "active"}`}
                onClick={() => handleStatisticsChange({ isQualityManagement: false })}
              >
                {t("beer_taps_informations")}
              </button>
            </div>

            <div className="bartrack-overview-section-stats">
              {isQualityManagement ? (
                <div className="bartrack-overview-section-stats-management">
                  <div className="bartrack-overview-section-stats-management-equipments">
                    <p className="bartrack-overview-section-stats-management-equipments-title">
                      {t("equipments_bartrack")}
                    </p>
                    <div className="bartrack-overview-section-stats-management-equipments-container">
                      {isBartracksLoading ? (
                        <>
                          <BartrackItem isLoading />
                          <BartrackItem isLoading />
                          <BartrackItem isLoading />
                        </>
                      ) : (
                        bartracks?.map((bartrack, index) => (
                          <BartrackItem bartrack={bartrack} index={index} key={bartrack.id} />
                        ))
                      )}
                    </div>
                  </div>

                  <div className="bartrack-overview-section-stats-management-informations">
                    <DatePickerHeader />

                    <div className="bartrack-overview-section-stats-management-informations-values">
                      <StatsCard
                        title={t("humidity")}
                        latest={qualitySummary?.stats?.latestHumi}
                        average={qualitySummary?.stats?.avgHumi}
                        max={qualitySummary?.stats?.maxHumi}
                        min={qualitySummary?.stats?.minHumi}
                        unit="%"
                        isLoading={isBartrackLoading}
                      />
                      <StatsCard
                        title={t("temperature")}
                        latest={qualitySummary?.stats?.latestTemp}
                        average={qualitySummary?.stats?.avgTemp}
                        max={qualitySummary?.stats?.maxTemp}
                        min={qualitySummary?.stats?.minTemp}
                        unit="°"
                        isLoading={isBartrackLoading}
                      />
                      <StatsCard
                        title={t("barometer")}
                        latest={qualitySummary?.stats?.latestBaro}
                        average={qualitySummary?.stats?.avgBaro}
                        max={qualitySummary?.stats?.maxBaro}
                        min={qualitySummary?.stats?.minBaro}
                        unit="bar"
                        isLoading={isBartrackLoading}
                      />
                    </div>

                    <div className="bartrack-overview-section-stats-management-informations-chart">
                      {currentBartrack?.pressureLimit &&
                        qualitySummary?.stats?.latestBaro > currentBartrack?.pressureLimit && (
                          <div className="bartrack-overview-section-stats-management-informations-chart-pressure-warning">
                            {t("pressure_too_high")}
                          </div>
                        )}

                      <div className="bartrack-overview-section-stats-management-informations-chart-buttons">
                        <button
                          className={chartToShow === "humidity" ? "active" : ""}
                          onClick={() => setChartToShow("humidity")}
                        >
                          {t("humidity")}
                        </button>
                        <button
                          className={chartToShow === "temperature" ? "active" : ""}
                          onClick={() => setChartToShow("temperature")}
                        >
                          {t("temperature")}
                        </button>
                        <button
                          className={chartToShow === "barometer" ? "active" : ""}
                          onClick={() => setChartToShow("barometer")}
                        >
                          {t("barometer")}
                        </button>
                      </div>

                      {currentBartrack && (
                        <>
                          {chartToShow === "barometer" && (
                            <ChartCard
                              key="barometer"
                              data={qualitySummaryDataToChartValues(
                                qualitySummary?.barometerData,
                                "baroMean",
                              )}
                              height={400}
                              widthPerData={100}
                              firstLineColor={"rgb(100, 200, 100)"}
                              title={currentBartrack.name}
                              yAxisUnit={"bar"}
                              name1={t("pressure")}
                              isLoading={isBartrackLoading}
                            />
                          )}

                          {chartToShow === "humidity" && (
                            <ChartCard
                              key="humidity"
                              data={qualitySummaryDataToChartValues(
                                qualitySummary?.humidityData,
                                "humiMean",
                              )}
                              height={400}
                              widthPerData={100}
                              firstLineColor={"rgb(87, 124, 255)"}
                              title={currentBartrack.name}
                              yAxisUnit={"%"}
                              name1={t("humidity")}
                              isLoading={isBartrackLoading}
                            />
                          )}

                          {chartToShow === "temperature" && (
                            <ChartCard
                              key="temperature"
                              data={qualitySummaryDataToChartValues(
                                qualitySummary?.tempData,
                                "tempMean",
                              )}
                              height={400}
                              widthPerData={100}
                              firstLineColor={"rgb(255, 164, 97)"}
                              title={currentBartrack.name}
                              yAxisUnit={"°"}
                              name1={t("temperature")}
                              isLoading={isBartrackLoading}
                            />
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="beer-tap">
                  <DatePickerHeader />

                  <div className="beer-tap-stats">
                    <div className="beer-tap-stats-container">
                      <Ordering
                        value={lines?.data
                          ?.reduce((sum, item) => sum + (item.volumeHectoLiters || 0), 0)
                          .toFixed(2)}
                        name={t("hectoliter_produces")}
                        icon={KegIcon}
                        isLoading={isBartrackLoading}
                      />
                      <Ordering
                        value={(
                          lines?.data?.reduce(
                            (sum, item) => sum + (item.volumeHectoLiters || 0),
                            0,
                          ) /
                          (BEER_EQUIVALENT_DIVISOR / 100000)
                        ).toFixed(2)}
                        name={t("drink_equivalent")}
                        icon={BeerIcon}
                        isLoading={isBartrackLoading}
                      />
                    </div>
                    <ChartCard
                      data={
                        lines?.pourData?.length > 0 || Object.keys(ordersVolumeByHour).length > 0
                          ? linesPourDataToChartValues()
                          : []
                      }
                      height={200}
                      widthPerData={100}
                      firstLineColor="#4200ff"
                      secondLineColor="#ff02c7"
                      yAxisUnit="ml"
                      name1={t("quantity_poured")}
                      name2={t("quantity_sold")}
                      isLoading={isBartrackLoading}
                    />
                  </div>
                  <div className="bartrack-overview-section secondary-section beer-tap-table">
                    <Table
                      columns={[
                        {
                          key: "icon",
                          label: "#",
                          render: (_, row, index) => (
                            <img src={row.imageUrl || LineDefault} alt={`Icon`} />
                          ),
                        },
                        {
                          key: "beverage_name",
                          label: t("line"),
                          render: (_, row, index) => row.name,
                        },
                        { key: "volumeHectoLiters", label: t("hectoliter") },
                        { key: "averageConsumptionPerDay", label: t("average_usage_ci") },
                      ]}
                      data={lines?.data}
                      title={t("history_lines")}
                      onClick={exportLinesToExcel}
                      buttonName={t("export")}
                      isLoading={isBartrackLoading}
                    />
                  </div>
                </div>
              )}
            </div>
          </section>

          <section className="bartrack-overview-section secondary-section">
            <p className="bartrack-overview-section-title">{t("lines")}</p>
            <div className="bartrack-overview-section-lines-container">
              {isLinesLoading ? (
                <>
                  <BeverageCard isLoading />
                  <BeverageCard isLoading />
                  <BeverageCard isLoading />
                </>
              ) : (
                lines?.data?.map((beverage) => (
                  <BeverageCard
                    key={beverage.id}
                    icon={beverage.imageUrl || LineDefault}
                    name={beverage.name}
                    onClick={() => navigateToLine(beverage)}
                    percentLoadNumber={(
                      ((beverage.container_volume_total_ml - beverage.pouredQuantity) /
                        beverage.container_volume_total_ml) *
                      100
                    ).toFixed(2)}
                  />
                ))
              )}
            </div>
          </section>

          <section className="bartrack-overview-section secondary-section">
            <div className="bartrack-overview-section-header">
              <p>{t("my_beverages")}</p>
            </div>
            <div
              className={`bartrack-overview-section-beverages-${!isProductsLoading && beverages.length === 0 ? "empty" : "container"}`}
            >
              {isProductsLoading ? (
                <>
                  <BeverageCard isLoading />
                  <BeverageCard isLoading />
                  <BeverageCard isLoading />
                </>
              ) : beverages.length > 0 ? (
                beverages.map((beverage) => (
                  <BeverageCard
                    key={beverage.id}
                    icon={getIcon(beverage.category, beverage?.customIcon)}
                    name={`${beverage.title} ${beverage.servingSize ? beverage.servingSize + "mL" : ""}`}
                  />
                ))
              ) : (
                <>
                  <p>{t("no_assigned_beverages")}</p>
                  <GradientButton onPress={navigateToPOS}>{t("go_to_pos")}</GradientButton>
                </>
              )}
            </div>
          </section>
      </div>
    </>
  );
}

export default BartrackOverview;
